import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, mainSiteUrl } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.quest';

const articleJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Гид по покупкам через YOYBUY Spreadsheet: все категории товаров',
    description: 'Полный обзор всех категорий товаров, доступных для заказа через YOYBUY Spreadsheet. Узнайте, что можно купить на Taobao, 1688 и других китайских платформах с помощью удобной таблицы заказов.',
    image: 'https://readdy.ai/api/search-image?query=Colorful%20organized%20retail%20display%20with%20diverse%20product%20categories%20including%20shoes%20clothing%20accessories%20bags%20and%20electronics%20arranged%20in%20neat%20sections%20on%20modern%20white%20shelving%2C%20bright%20cheerful%20department%20store%20atmosphere%2C%20wide%20angle%20view%20showing%20variety%2C%20inclusive%20shopping%20experience%20concept%2C%20warm%20welcoming%20commercial%20photography&width=1600&height=800&seq=shopping-guide-hero&orientation=landscape',
    datePublished: '2026-07-04',
    dateModified: '2026-07-04',
    author: { '@type': 'Organization', name: 'YOYBUY Spreadsheet' },
    publisher: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/article/shopping-guide` },
    inLanguage: 'ru',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Гид по покупкам', item: `${siteUrl}/article/shopping-guide` },
    ],
  },
];

export default function ShoppingGuide() {
  return (
    <>
      <JsonLd data={articleJsonLd[0]} />
      <JsonLd data={articleJsonLd[1]} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-14 md:pb-20">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap">
                <li><a href="/" className="hover:text-primary-500 transition-colors no-underline">Главная</a></li>
                <li><i className="ri-arrow-right-s-line text-xs w-3 h-3 flex items-center justify-center"></i></li>
                <li className="text-foreground-600 font-medium">Гид по покупкам</li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-wider uppercase text-primary-600 bg-primary-50 rounded-full">
                Гид по покупкам
              </span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Гид по покупкам через YOYBUY Spreadsheet: все категории товаров
              </h1>
              <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
                Полный обзор всех категорий товаров, доступных для заказа через YOYBUY Spreadsheet. Узнайте, что можно купить на Taobao, 1688 и других китайских платформах с помощью удобной таблицы заказов.
              </p>
            </header>

            <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px] mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Colorful%20organized%20retail%20display%20with%20diverse%20product%20categories%20including%20shoes%20clothing%20accessories%20bags%20and%20electronics%20arranged%20in%20neat%20sections%20on%20modern%20white%20shelving%2C%20bright%20cheerful%20department%20store%20atmosphere%2C%20wide%20angle%20view%20showing%20variety%2C%20inclusive%20shopping%20experience%20concept%2C%20warm%20welcoming%20commercial%20photography&width=1600&height=800&seq=shopping-guide-hero&orientation=landscape"
                alt="Гид по покупкам YOYBUY Spreadsheet"
                title="Гид по покупкам YOYBUY Spreadsheet"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Как покупать через YOYBUY Spreadsheet
                </h2>
                <p className="text-sm md:text-base leading-relaxed mb-4">
                  <strong>YOYBUY Spreadsheet</strong> делает процесс заказа из Китая невероятно простым. Вместо того чтобы оформлять каждый товар по отдельности, вы заполняете одну таблицу со всеми позициями — и система обрабатывает их одновременно. Это экономит часы времени и практически исключает ошибки.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  <div className="bg-background-100 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-800 mb-1">Шаг 1</h3>
                    <p className="text-xs text-foreground-500">Заполните таблицу товарами</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-accent-50 flex items-center justify-center">
                      <i className="ri-send-plane-line text-accent-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-800 mb-1">Шаг 2</h3>
                    <p className="text-xs text-foreground-500">Отправьте заказ</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-secondary-50 flex items-center justify-center">
                      <i className="ri-box-3-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-800 mb-1">Шаг 3</h3>
                    <p className="text-xs text-foreground-500">Получите товары</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Доступные категории товаров
                </h2>
                <p className="text-sm md:text-base leading-relaxed mb-6">
                  Через <strong>YOYBUY Spreadsheet</strong> вы можете заказать товары из следующих категорий. Нажмите на любую категорию, чтобы перейти к выбору товаров на kaspreadsheet.com:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 p-4 bg-background-100 rounded-xl hover:bg-primary-50 transition-colors no-underline group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center group-hover:border-primary-200 transition-colors shrink-0">
                        <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-foreground-700 group-hover:text-primary-600 transition-colors">{cat.nameRu}</span>
                        <span className="text-xs text-foreground-400 ml-2">({cat.name})</span>
                      </div>
                    </a>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Советы для успешных покупок
                </h2>
                <ul className="space-y-3 text-sm md:text-base">
                  <li className="flex gap-3"><span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span><span><strong>Проверяйте рейтинг продавца</strong> — чем выше рейтинг и больше отзывов, тем надёжнее сделка.</span></li>
                  <li className="flex gap-3"><span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span><span><strong>Сравнивайте цены</strong> — на разных платформах один и тот же товар может стоить по-разному.</span></li>
                  <li className="flex gap-3"><span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span><span><strong>Учитывайте сезонность</strong> — заказывайте сезонные товары заранее, чтобы избежать задержек.</span></li>
                  <li className="flex gap-3"><span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span><span><strong>Консолидируйте заказы</strong> — объединяйте товары от разных продавцов в одну посылку для экономии на доставке.</span></li>
                </ul>
              </section>

              <section className="bg-background-100 rounded-2xl p-6 md:p-8 text-center">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  Начните покупки прямо сейчас
                </h2>
                <p className="text-sm md:text-base text-foreground-500 mb-6 max-w-md mx-auto leading-relaxed">
                  Выберите категорию, заполните таблицу YOYBUY Spreadsheet и получите товары из Китая быстрее, чем когда-либо.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap no-underline">
                    Перейти к YOYBUY
                    <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <a href="/" className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-foreground-700 border border-background-300 hover:border-primary-300 hover:text-primary-500 rounded-full transition-colors whitespace-nowrap no-underline">
                    На главную
                    <i className="ri-home-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}